import * as React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faArrowDown,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
  BloodworkAnalysis,
  RootCauseCategory,
  FurtherConsiderations,
  Section,
} from '../../models';
import { stripSupplementUrl } from '../../utils/supplementItem';
import { parseHoverText } from '../../utils/hoverText';
import { NAV_TOKEN_PARSE, NAV_TOKEN_SPLIT } from '../../utils/navToken';
import { nutrientCategoryOrder } from './nutrient-category-order';

// The DB authors up/down status as the Unicode triangles ▲ / ▼ in the
// Interpretation and Causes text (e.g. "Logic: ▼ WBC and ▲ NLR"). Swap those
// for Font Awesome arrow icons at render time so the stored data stays
// plain-text while the report shows real arrows. Note: the app is on Font
// Awesome 5 via the React SVG component (@fortawesome/react-fontawesome) — no
// web-font CSS is loaded, so raw `<i class="fa-…">` markup would render
// nothing; FontAwesomeIcon is the supported path here.
const ARROW_ICON_STYLE: React.CSSProperties = {
  fontSize: '1.1em',
  verticalAlign: '-0.1em',
};

// width/height ATTRIBUTES on the <svg>, without which these arrows are missing
// from every exported PDF while looking correct on screen.
//
// html2canvas rasterizes an inline <svg> by serializing it to a
// `data:image/svg+xml,…` URL and loading that back as an image. Serializing
// carries the element's attributes and inline style but NOT the stylesheet —
// and Font Awesome sizes its icons purely from external class rules
// (`.svg-inline--fa{height:1em}`, `.fa-w-14{width:.875em}`). Stripped of those,
// the standalone SVG has no intrinsic size and html2canvas paints nothing.
//
// The VALUE is arbitrary beyond being non-zero: html2canvas scales the image
// into the element's layout box, so the arrow still prints at whatever size the
// surrounding text gives it (verified at both an 11px and a 24px context).
// `as {}` because react-fontawesome forwards unrecognised props straight onto
// the <svg> (see `extraProps` in its index.js) but types only DOM events.
const ARROW_ICON_ATTRS = { width: 16, height: 16 } as {};

// Both spellings of an up/down marker map to the same icon: ▲ / ▼ as the DB
// authors them, and ↑ / ↓ as the Notes textarea stores them (see
// trianglesToArrows). Keeping one splitter means the tab, the digest and the
// saved note all draw the identical arrow — and notes written before the
// textarea switched to ↑ / ↓ render as arrows too, rather than stranding their
// triangles.
const ARROW_CHARS = /[▲▼↑↓]/;

// Turn those markers into styled FontAwesome up/down arrows. Exported so the
// Notes "gather" digest and the saved-note renderer show the same arrows the
// tab does instead of raw characters, whose weight and size depend on whatever
// font the viewer's machine resolves.
export function renderArrows(
  text: string,
  keyPrefix: string | number,
): React.ReactNode {
  if (!text || !ARROW_CHARS.test(text)) {
    return text;
  }
  return text.split(/([▲▼↑↓])/).map((part, i) => {
    if (part === '▲' || part === '↑') {
      return (
        <FontAwesomeIcon
          key={`${keyPrefix}-up-${i}`}
          icon={faArrowUp}
          style={ARROW_ICON_STYLE}
          {...ARROW_ICON_ATTRS}
        />
      );
    }
    if (part === '▼' || part === '↓') {
      return (
        <FontAwesomeIcon
          key={`${keyPrefix}-down-${i}`}
          icon={faArrowDown}
          style={ARROW_ICON_STYLE}
          {...ARROW_ICON_ATTRS}
        />
      );
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

// Plain-text counterpart to renderArrows, for the places that can only hold a
// string — the Notes textarea and the clipboard. An SVG icon can't live in a
// textarea, so swap the DB's ▲ / ▼ for the Unicode arrows ↑ / ↓, which survive
// the note round-trip (textarea → save → Section Notes panel → PDF) and still
// read as arrows rather than the old triangles.
export function trianglesToArrows(text: string): string {
  return (text ?? '').replace(/▲/g, '↑').replace(/▼/g, '↓');
}

// Inverse of trianglesToArrows. Used only to recognise notes saved BEFORE
// Insert did this conversion, so their inserted blocks still match. Both maps
// are character-for-character, so a converted string keeps its original length
// and any index arithmetic on it stays valid.
export function arrowsToTriangles(text: string): string {
  return (text ?? '').replace(/↑/g, '▲').replace(/↓/g, '▼');
}

// References bullets end with "PMID: 12345678" or "PMID: 12345678, 23456789, …".
// Linkify each PMID number to PubMed so practitioners can jump straight to the
// citation. Sheet cells already hyperlink them, but the gviz CSV export strips
// hyperlinks, so we reconstruct deterministically on the client.
const PMID_PREFIX_RE = /\bPMID:\s*/i;

// A pattern's References row (subId 4 / 5) now stores Reference Library tag
// slugs in items[], not full citations. A tag is letters (either case),
// digits, and underscores — anything else (has spaces/punctuation) is treated
// as a legacy inline citation and falls through to the original PMID-linkifier.
// Case is allowed both ways so PRC-library tags (e.g. NEUT_1, ZINC_1) match the
// spreadsheet's uppercase convention while legacy Glucose tags stay lowercase.
const TAG_RE = /^[a-zA-Z0-9_]+$/;

// Parses a Reference Library row's combined items[0] back into its parts.
// pullGlucoseReferenceLibrary.js builds it deterministically as:
//   "<citation> PMID: <num> <url>"
//   "<citation> Bookshelf ID: NBK<num> <url>"
//   "<citation> <url>"          (no identifier)
//   "<citation>"                (citation only)
// Parse from the END so the citation (which may contain digits or colons)
// is whatever's left over.
function parseLibraryCitation(combined: string): {
  citation: string;
  identifier?: string;
  identifierNumber?: string;
  url?: string;
} {
  let working = combined.trim();
  let url: string | undefined;
  const urlMatch = working.match(/\s+(https?:\/\/\S+)\s*$/);
  if (urlMatch) {
    url = urlMatch[1];
    working = working.slice(0, urlMatch.index).trimEnd();
  }
  let identifier: string | undefined;
  let identifierNumber: string | undefined;
  const pmidMatch = working.match(/\s+PMID:\s*(\d+)\s*$/i);
  const bookshelfMatch = working.match(/\s+Bookshelf ID:\s*(NBK\d+)\s*$/i);
  if (pmidMatch) {
    identifier = `PMID: ${pmidMatch[1]}`;
    identifierNumber = pmidMatch[1];
    working = working.slice(0, pmidMatch.index).trimEnd();
  } else if (bookshelfMatch) {
    identifier = `Bookshelf ID: ${bookshelfMatch[1]}`;
    identifierNumber = bookshelfMatch[1];
    working = working.slice(0, bookshelfMatch.index).trimEnd();
  }
  return { citation: working, identifier, identifierNumber, url };
}

// Old format: items[] contained full citation strings. Linkify any PMID
// numbers and render the rest as plain text. Kept as a fallback for any
// References row that hasn't been migrated to tag-based items.
function renderLegacyCitation(item: string): JSX.Element {
  const match = PMID_PREFIX_RE.exec(item);
  if (!match) return <>{item}</>;
  const splitAt = match.index + match[0].length;
  const before = item.slice(0, splitAt);
  const after = item.slice(splitAt);
  const parts = after.split(/(\d+)/);
  return (
    <>
      {before}
      {parts.map((part, idx) =>
        /^\d+$/.test(part) ? (
          <a
            key={idx}
            href={`https://pubmed.ncbi.nlm.nih.gov/${part}/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={idx}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

// Library reference tags are UPPERCASE and end in their citation number, which
// mirrors the inline "(n)" markers in the Possible Root Cause text, so we
// surface it in the References list too:
//
//   NEUT_1, ZINC_6, NAK_10        one prefix segment
//   ADRENAL_G1_11, ADRENAL_G4_17  a group segment too (SECTION_Gn_NN), where NN
//                                 runs section-wide rather than restarting per
//                                 group, so two groups cannot claim one number.
//
// Reading the LAST segment is what admits the grouped shape, and matches the
// regex buildCitationUrlMap has always used to resolve those same markers.
//
// The uppercase requirement is load-bearing, not decoration: it is what keeps
// Glucose's lowercase descriptive slugs unnumbered. "(2026)" would otherwise be
// pulled off the tail of ada_diagnosis_classification_diabetes_2026 and printed
// as a citation number.
function referenceNumber(item: string): string | null {
  const m = /^[A-Z][A-Z0-9_]*_(\d+)$/.exec(item);
  return m ? m[1] : null;
}

// A numbered list reads in number order. Rows store their tags as the sheet
// lists them, which sorts lexically — "(1) (10) (4) (6) (9)" down the page.
export function sortByReferenceNumber(items: string[]): string[] {
  return [...items].sort(
    (a, b) => Number(referenceNumber(a)) - Number(referenceNumber(b)),
  );
}

// A References list is styled as a WHOLE, never item by item, because the two
// tag styles carry different meaning and mixing them in one list reads as a
// rendering fault:
//
//   every item numbered  -> bibliography styling, no bullets, each "(n)" shown.
//                           The number has a referent: these are the Possible
//                           Root Cause References rows, and the marker's Causes
//                           text cites those same numbers inline.
//   anything unnumbered  -> bullets on every item, no numbers shown at all.
//
// The second case is now Glucose alone: its lowercase descriptive slugs
// (insulin_resistance_statpearls) carry no number, and nothing in a glucose
// pattern's text cites one. A row that also happened to carry a numbered tag
// was printing a stray "(n)" beside a column of bulleted citations, pointing at
// a numbering that lives in a different row's Causes text.
//
// Stress & Adrenal used to land here too. Its library tags were renumbered
// section-wide in 2026-09 (ADRENAL_G1_01 -> ADRENAL_G1_11, continuing after
// NAK's 1-10), so every adrenal Pattern References row is fully numbered and
// takes the first branch. Renumbering Glucose the same way would mean 47 slug
// tags and a matching sheet edit.
//
// A hanging indent was tried here and reverted: pulling the "(n)" into its own
// gutter left it stranded in the margin, well clear of the citation, and
// indented every unnumbered line behind an empty column.
const referenceListItemStyle: React.CSSProperties = {
  listStyleType: 'none',
};

// True when every item in a References row resolves to a number, which is what
// earns the row its bibliography styling.
export function isNumberedReferenceList(items: string[]): boolean {
  return (
    Array.isArray(items) &&
    items.length > 0 &&
    items.every(item => referenceNumber(item))
  );
}

// New format: items[] holds Reference Library tag slugs. Resolve each to its
// library row and render "(n) <citation>  —  <identifier>" where the identifier
// (PMID or Bookshelf ID) links to the source URL, and the leading "(n)" (when
// present) matches the inline citation markers. Falls back to the legacy
// renderer for non-tag items so non-migrated sections keep working.
function renderReferenceItem(
  item: string,
  furtherConsiderations: FurtherConsiderations,
  showNumbers: boolean,
): JSX.Element {
  const num = showNumbers ? referenceNumber(item) : null;
  const prefix = num ? `(${num}) ` : '';
  if (!TAG_RE.test(item)) {
    return (
      <>
        {prefix}
        {renderLegacyCitation(item)}
      </>
    );
  }
  const libraryRow = furtherConsiderations.findByTag(item);
  if (!libraryRow || !libraryRow.items || libraryRow.items.length === 0) {
    // Unknown tag — surface the raw slug so a missing library row is visible
    // rather than silently empty.
    return (
      <>
        {prefix}
        {item}
      </>
    );
  }
  const { citation, identifier, url } = parseLibraryCitation(libraryRow.items[0]);
  if (!identifier) {
    // No PMID / Bookshelf ID — render citation, optionally followed by the URL.
    return (
      <>
        {prefix}
        {citation}
        {url ? (
          <>
            {'  —  '}
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          </>
        ) : null}
      </>
    );
  }
  return (
    <>
      {prefix}
      {citation}
      {'  —  '}
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {identifier}
        </a>
      ) : (
        <>{identifier}</>
      )}
    </>
  );
}

// Plain-text form of a References item for the Notes "gather" digest, mirroring
// renderReferenceItem: "(n) <citation> — <identifier|url>". Resolves a library
// tag slug via findByTag; falls back to the raw string for legacy/unknown items.
// So the digest's References card reads like the tab (and gives the "(n)" markers
// in the Possible Root Causes text a home) as plain text that inserts cleanly.
//
// showNumbers carries the tab's per-row rule across: a row the tab bullets
// shows no numbers there either, so the digest doesn't reintroduce a "(4)" the
// screen deliberately drops. Callers pass isNumberedReferenceList(row.items).
export function referenceItemToText(
  item: string,
  furtherConsiderations: FurtherConsiderations,
  showNumbers: boolean = true,
): string {
  const num = showNumbers ? referenceNumber(item) : null;
  const prefix = num ? `(${num}) ` : '';
  if (!TAG_RE.test(item)) {
    return `${prefix}${item}`;
  }
  const libraryRow = furtherConsiderations.findByTag(item);
  if (!libraryRow || !libraryRow.items || libraryRow.items.length === 0) {
    return `${prefix}${item}`;
  }
  const { citation, identifier, url } = parseLibraryCitation(libraryRow.items[0]);
  let out = `${prefix}${citation}`;
  if (identifier) out += ` — ${identifier}`;
  else if (url) out += ` — ${url}`;
  return out;
}

// Build a { "<n>": url } map for a marker so inline "(n)" citation markers in
// its Causes/Symptoms text can link out. The number matches a tag's numeric
// suffix (e.g. "(6)" → tag ZINC_6 → its library URL), read from the marker's
// References row (subCategoryId 4) and resolved via findByTag.
//
// Shares referenceNumber with the tab so a tag cannot be numbered in one place
// and not the other. This used to run its own looser /_(\d+)$/, which read a
// number off shapes the tab refused to print one for: it mapped ADRENAL_G1_01
// and ADRENAL_G2_01 both to "01" (last write winning), and it would key
// Glucose's ada_..._2026 slug under "2026", so a bare year in parentheses
// anywhere in that marker's Causes text became a link to the ADA standards.
function buildCitationUrlMap(
  rootCauseCategory: number,
  section: string,
  furtherConsiderations: FurtherConsiderations,
): { [n: string]: string } {
  const map: { [n: string]: string } = {};
  const refRows = furtherConsiderations.findConsiderations(
    rootCauseCategory,
    section,
    'References',
  );
  for (const row of refRows) {
    for (const tag of row.items || []) {
      const num = referenceNumber(tag);
      if (!num) continue;
      const lib = furtherConsiderations.findByTag(tag);
      if (!lib || !lib.items || !lib.items.length) continue;
      const { url } = parseLibraryCitation(lib.items[0]);
      if (url) map[num] = url;
    }
  }
  return map;
}

// Sections that predate the research pass cite their sources inline as raw
// "PMID: 12345678" text rather than the "(n)" markers a researched row uses.
// The References tab has always linkified those (see renderLegacyCitation), but
// the Causes tab printed them as dead text.
//
// Matched as a whole TOKEN so only digits introduced by a "PMID" label become
// links — a bare number elsewhere in the sentence (a dose, a year, a journal
// volume, "40(1):61-66") is left alone. Both authored spellings are covered:
// with or without the colon, and a comma list that either repeats the label
// ("PMID 2702751, PMID 5884648") or doesn't ("PMID: 33931563, 30915507").
const PMID_TOKEN_SOURCE =
  '\\bPMID\\s*:?\\s*\\d{4,9}(?:\\s*,\\s*(?:PMID\\s*:?\\s*)?\\d{4,9})*';
// Split needs the `g` flag and a capture group to keep the tokens; the test
// counterpart is anchored and NON-global, because a global regex carries
// lastIndex between .test() calls and would skip every other match.
const PMID_TOKEN_SPLIT = new RegExp(`(${PMID_TOKEN_SOURCE})`, 'gi');
const PMID_TOKEN_TEST = new RegExp(`^${PMID_TOKEN_SOURCE}$`, 'i');

// Linkify the numbers inside one matched "PMID: …" token, keeping the label,
// colon and commas as plain text.
function renderPmidToken(
  token: string,
  keyPrefix: string | number,
): React.ReactNode {
  return token.split(/(\d{4,9})/).map((part, i) =>
    /^\d{4,9}$/.test(part) ? (
      <a
        key={`${keyPrefix}-pmid-${i}`}
        href={`https://pubmed.ncbi.nlm.nih.gov/${part}/`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={`${keyPrefix}-pt-${i}`}>{part}</React.Fragment>
    ),
  );
}

// One plain-text run of a bullet: raw "PMID: …" citations become PubMed links,
// everything else goes through the ▲/▼ arrow renderer exactly as before. The
// PMID check short-circuits so bullets without one take the original path.
function renderTextRun(
  text: string,
  keyPrefix: string | number,
): React.ReactNode {
  if (!text || text.indexOf('PMID') === -1) {
    return renderArrows(text, keyPrefix);
  }
  return text.split(PMID_TOKEN_SPLIT).map((part, i) =>
    PMID_TOKEN_TEST.test(part) ? (
      <React.Fragment key={`${keyPrefix}-p-${i}`}>
        {renderPmidToken(part, `${keyPrefix}-${i}`)}
      </React.Fragment>
    ) : (
      <React.Fragment key={`${keyPrefix}-a-${i}`}>
        {renderArrows(part, `${keyPrefix}-${i}`)}
      </React.Fragment>
    ),
  );
}

// Render a plain-text item, turning inline "(n)" markers into links to the
// matching reference URL. Numbers with no known URL stay as plain text, so
// sections without a References row are unaffected.
function renderCitedText(
  item: string,
  urlMap: { [n: string]: string },
): JSX.Element | string {
  if (!urlMap || Object.keys(urlMap).length === 0) {
    return <>{renderTextRun(item, 'arr')}</>;
  }
  // Split out citation groups — a single "(6)" or a multi like "(3, 5)".
  const parts = item.split(/(\((?:\d+\s*,\s*)*\d+\))/g);
  return (
    <>
      {parts.map((part, i) => {
        const group = /^\(((?:\d+\s*,\s*)*\d+)\)$/.exec(part);
        if (!group) {
          return (
            <React.Fragment key={i}>{renderTextRun(part, i)}</React.Fragment>
          );
        }
        const nums = group[1].split(/\s*,\s*/);
        // Leave untouched if none of the numbers resolve to a reference.
        if (!nums.some(n => urlMap[n])) {
          return <React.Fragment key={i}>{part}</React.Fragment>;
        }
        // Keep the parens/commas as text; link each number (bold, so it's
        // clearly clickable) to its own reference.
        return (
          <React.Fragment key={i}>
            {'('}
            {nums.map((n, j) => (
              <React.Fragment key={j}>
                {j > 0 ? ', ' : ''}
                {urlMap[n] ? (
                  <a
                    href={urlMap[n]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 'bold' }}
                  >
                    {n}
                  </a>
                ) : (
                  n
                )}
              </React.Fragment>
            ))}
            {')'}
          </React.Fragment>
        );
      })}
    </>
  );
}

// A cross-section pointer embedded in Causes text as
//   [[nav:<Section>:<tab?>|<label>]]
// e.g. [[nav:Proteins:causes|Protein Status]] links to the Proteins section and
// opens its Possible Root Causes tab; [[nav:RBC|Blood Oxygen & Red Blood Cell
// Function]] just scrolls to the RBC section (which has no Causes tab). Clicking
// dispatches a window event handled by BloodworkTestAnalysis — which owns the
// section collapse state and the scroll — so the link needs no prop wiring down
// through the tab tree.
// Shared with the plain-text surfaces (Notes digest, AI export) via
// utils/navToken, so the renderer and they can't disagree about what a token is.

function renderNavLink(
  section: string,
  tab: string | undefined,
  label: string,
  key: React.Key,
): JSX.Element {
  return (
    <a
      key={key}
      href="#"
      className="xref-section-link"
      style={{ fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
      onClick={e => {
        e.preventDefault();
        window.dispatchEvent(
          new CustomEvent('labsmarts:navigate-section', {
            detail: { section, tab: tab || null },
          }),
        );
      }}
    >
      {label}
    </a>
  );
}

// Render the visible body of a Causes/Interpretation item: first turn any
// [[nav:…]] cross-section pointers into clickable links, then pass the
// surrounding text through the existing "(n)" citation + ▲/▼ arrow renderer.
// Items with no nav token are handed straight to renderCitedText, so behaviour
// there is unchanged.
function renderItemBody(
  item: string,
  urlMap: { [n: string]: string },
): JSX.Element | string {
  if (item.indexOf('[[nav:') === -1) {
    return renderCitedText(item, urlMap);
  }
  const parts = item.split(NAV_TOKEN_SPLIT);
  return (
    <>
      {parts.map((part, i) => {
        const m = NAV_TOKEN_PARSE.exec(part);
        if (m) {
          return renderNavLink(m[1], m[2], m[3], `nav-${i}`);
        }
        return (
          <React.Fragment key={i}>{renderCitedText(part, urlMap)}</React.Fragment>
        );
      })}
    </>
  );
}

// The tooltip's own id, so index.css can widen it past Bootstrap's 200px
// default — these explainers are full sentences. One id is enough because only
// one tooltip is ever open at a time (same approach as #ethnicity-tooltip).
const HOVER_TOOLTIP_ID = 'prc-hover-tooltip';

// Render an item's "[Hover text: …]" explainer for the PDF. Hover doesn't exist
// on paper and the PDF is the client's copy, so the explanation prints under
// the bullet instead of being dropped.
function renderPdfHoverNote(hover: string): JSX.Element {
  return (
    <div
      style={{
        fontSize: '0.9em',
        fontStyle: 'italic',
        opacity: 0.85,
        marginTop: '2px',
      }}
    >
      {hover}
    </div>
  );
}

/**
 * Render one Causes/Interpretation bullet.
 *
 * A bullet may carry a plain-language explainer for a clinical term, authored
 * inline as "[Hover text: …]" (see utils/hoverText). The token itself is never
 * printed. On screen the whole bullet becomes the hover target — the explainer
 * defines the cause, not one word inside it — marked with a dotted underline
 * and a trailing info icon so it reads as hoverable. In the PDF it prints
 * beneath the bullet instead.
 *
 * Bullets with no explainer render exactly as before. Exported for the render
 * tests.
 */
export function renderConsiderationItem(
  item: string,
  urlMap: { [n: string]: string },
  forPdf?: boolean,
): JSX.Element | string {
  const { text, hover } = parseHoverText(item);
  const body = renderItemBody(text, urlMap);
  if (!hover) {
    return body;
  }
  if (forPdf) {
    return (
      <>
        {body}
        {renderPdfHoverNote(hover)}
      </>
    );
  }
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={HOVER_TOOLTIP_ID}>{hover}</Tooltip>}
    >
      <span
        style={{
          borderBottom: '1px dotted currentColor',
          cursor: 'help',
        }}
      >
        {body}
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{ marginLeft: '5px', fontSize: '0.85em', opacity: 0.6 }}
          {...ARROW_ICON_ATTRS}
        />
      </span>
    </OverlayTrigger>
  );
}

function renderSupplementItem(
  item: string,
  furtherConsiderations: FurtherConsiderations,
): JSX.Element {
  // resolveSupplement handles both forms: a supplement-library tag (resolved
  // via findByTag) and a legacy inline "Name — Description — url" string.
  const parsed = furtherConsiderations.resolveSupplement(item);
  if (parsed) {
    const { name, description, link } = parsed;
    const nameNode = link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontWeight: 'bold' }}
      >
        {name}
      </a>
    ) : (
      <strong>{name}</strong>
    );
    return (
      <>
        {nameNode}
        {description ? <> — {description}</> : null}
      </>
    );
  }
  // Promo lines and other unstructured items fall through unchanged, but strip
  // any trailing URL so it never leaks as raw text.
  return <>{stripSupplementUrl(item).text}</>;
}

/*
 * The brand discount line ("Use code LABSMARTS10 …").
 *
 * It is baked into the DATA too, appended as the last bullet of every
 * Supplements row by applySupplementLinks.js at ingest, so it arrives repeated
 * once per root-cause group — nine times over on a section like Vitamins &
 * Minerals, where it buried the actual recommendations. It is stripped from the
 * bullets and shown ONCE above the tab.
 *
 * Matched on the "Use code" prefix rather than the exact sentence because each
 * brand carries its own wording (reapplyBwhLabs.js injects a different one), and
 * a section showing two brands must surface both codes, not just the first.
 *
 * PRACTITIONER-ONLY. This component renders the on-screen tabs and nothing else;
 * the PDF builds supplements through its own path, which drops these lines
 * (see supplements-summary-panel.tsx). Keep it that way — the exported report is
 * the client's document and the discount is a trade term.
 */
const isPromoLine = (item: string): boolean => /^\s*use code /i.test(item || '');

/*
 * The canonical discount line PER BRAND, so the header no longer depends on the
 * data carrying one.
 *
 * The bar used to be assembled purely from the "Use code …" bullets found in the
 * rows, which meant a section whose pull script never called withPromoLine()
 * silently rendered no bar at all — exactly what happened to Stress & Adrenal
 * Balance, whose 19 cat 90xxx rows carry no such bullet. Deriving it from the
 * brands actually on the tab makes the bar structural: a new section gets it the
 * moment its rows render, with no re-pull and no DB write.
 *
 * Text matches what the ingest scripts write verbatim (applySupplementLinks.js
 * PROMO_LINE for Systemic Formulas, reapplyBwhLabs.js PROMO for BWH Labs), so
 * for the sections that DO carry the line nothing changes on screen. Keep the
 * two in step if either is ever reworded.
 */
const BRAND_PROMO_LINES: { [brand: string]: string } = {
  systemicFormulas:
    'Use code LABSMARTS10 to get 10% off your first Systemic Formulas order.',
  bwhLabs: 'Use code LABSMARTS10 to get 10% off your first order.',
};

// Rows written before the `brand` field existed (RBC's cat 9 "Vitamin C
// Insufficiency" and cat 10000000 "Supplementation Considerations") carry no
// brand. findConsiderations filters those out under any non-empty brand list, so
// today they never reach a tab at all — but if they are ever branded or the
// filter is relaxed, the discount is a standing trade term rather than a per-row
// one, so they read as the default brand instead of dropping the bar for the
// whole tab.
const DEFAULT_PROMO_BRAND = 'systemicFormulas';

/*
 * Every distinct discount line for the rows a Supplements tab is about to
 * render, in the order those brands first appear.
 *
 * Driven off the SAME rows the tab renders, so a code can never appear here for
 * a brand whose rows are filtered out (an unchecked Supplement Settings brand),
 * or go missing for one that is shown. A line the row itself carries wins over
 * the canonical one, so a brand that reworded its promo in the data still shows
 * its own wording.
 */
export function promoLinesFor(
  rows: Array<{ brand?: string; items?: string[] }>,
): string[] {
  const byBrand = new Map<string, string[]>();
  for (const row of rows) {
    const brand = row.brand || DEFAULT_PROMO_BRAND;
    const carried = byBrand.get(brand) || [];
    byBrand.set(
      brand,
      carried.concat((row.items || []).filter(isPromoLine)),
    );
  }
  const lines: string[] = [];
  byBrand.forEach((carried, brand) => {
    const line = carried.length
      ? stripSupplementUrl(carried[0]).text
      : BRAND_PROMO_LINES[brand] || BRAND_PROMO_LINES[DEFAULT_PROMO_BRAND];
    if (line && lines.indexOf(line) === -1) lines.push(line);
  });
  return lines;
}

// Rule: a Supplements row that resolves to exactly ONE supplement reads
// "Supporting Supplement for …" (singular); two or more keep the stored plural
// "Supplements". Applied at render so titles can stay canonically plural in the
// data/sheet. Promo and other unstructured bullets (e.g. the "Use code …" line)
// don't resolve to a supplement, so they never count toward the total.
function supplementTitle(
  title: string,
  items: string[],
  furtherConsiderations: FurtherConsiderations,
): string {
  const supplementCount = (items || []).filter(
    item => furtherConsiderations.resolveSupplement(item) !== null,
  ).length;
  return supplementCount === 1
    ? title.replace(/\bSupplements\b/, 'Supplement')
    : title;
}

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  section: Section;
  tab: string;
  brands?: string[];
  // Set by PDFTab. Switches "[Hover text: …]" explainers from a tooltip to
  // printed text, since the PDF can't be hovered.
  forPdf?: boolean;
};

// WBC: float the 110xx “Immune System Abnormality …” drivers to the top.
// Glucose: float the 710xx Glucose Dysregulation Likelihood drivers to the top —
// same pattern, so the gauge's "why this %" reads first.
// Vitamins & Minerals: order every category by how concerning its marker is,
// so the Supplements tab leads with the Priority markers — see
// nutrientCategoryOrder. That one needs the analysis to read marker statuses
// from, which is why `bloodworkAnalysis` is a parameter; without it the section
// keeps its original order. Other sections keep their original order.
// Exported so the Notes "gather" digest can present each
// tab's considerations in the SAME order the tab itself shows them.
export function prioritizeRootCauseCategories<T extends { rootCauseCategory: RootCauseCategory }>(
  section: Section,
  categories: T[],
  bloodworkAnalysis?: BloodworkAnalysis,
): T[] {
  const floatRange = (lo: number, hi: number) =>
    categories
      .map((c, idx) => {
        const id = Number(c.rootCauseCategory);
        const inRange = id >= lo && id <= hi;
        return { c, idx, id, inRange };
      })
      .sort((a, b) => {
        if (a.inRange !== b.inRange) return a.inRange ? -1 : 1; // in-range group first
        if (a.inRange && b.inRange) return b.id - a.id; // within it, higher tier first
        return a.idx - b.idx; // everything else keeps original order
      })
      .map(x => x.c);

  if (section === Section.WBC) return floatRange(11010, 11099);
  if (section === Section.GLUCOSE) return floatRange(71000, 71099);
  if (section === Section.NUTRIENTS) return nutrientCategoryOrder(categories, bloodworkAnalysis);
  return categories;
}

export class FurtherConsiderationsSection extends React.Component<Props, {}> {
  furtherConsiderationsForPossibleRootCauses(
    bloodworkAnalysis: BloodworkAnalysis,
  ): JSX.Element {
    const { section } = this.props;

    const { furtherConsiderations, tab, brands } = this.props;
    // `tab` is passed so the adrenal section can widen Causes/References to the
    // markers a combination pattern covers — see possibleRootCauseCategoriesFor.
    const categories =
      bloodworkAnalysis.possibleRootCauseCategoriesFor(section, tab) || [];
    const prioritized = prioritizeRootCauseCategories(
      section,
      categories,
      bloodworkAnalysis,
    );

    /*
     * The discount line(s) for this tab, hoisted above the rows. Collected from
     * the SAME query the rows use — see promoLinesFor for why the brand, not the
     * data, is what decides which codes show.
     */
    const promoLines =
      tab === 'Supplements'
        ? promoLinesFor(
            prioritized.flatMap(category =>
              furtherConsiderations.findConsiderations(
                category.rootCauseCategory,
                section,
                tab,
                brands,
              ),
            ),
          )
        : [];

    return (
      <span>
        {promoLines.length > 0 && (
          <div className="supplements-promo-header">
            {promoLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
        {prioritized.map(category => {
          return this.furtherConsiderationsFor(category.rootCauseCategory);
        })}
      </span>
    );
  }
  furtherConsiderationsFor(rootCauseCategory: RootCauseCategory): JSX.Element {
    const { furtherConsiderations, section, tab, brands, forPdf } = this.props;
    // Inline "(n)" citation markers (in Causes/Symptoms text) link to the
    // marker's References; skipped for tabs that have their own renderers.
    const citationUrlMap =
      tab === 'Supplements' || tab === 'References'
        ? {}
        : buildCitationUrlMap(rootCauseCategory, section, furtherConsiderations);
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab, brands)
            .map((consideration, cindex) => {
              // Decided once per row, not per item — see referenceListItemStyle.
              const numberedList =
                tab === 'References' &&
                isNumberedReferenceList(consideration.items);
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {renderArrows(
                      tab === 'Supplements'
                        ? supplementTitle(
                            consideration.title,
                            consideration.items,
                            furtherConsiderations,
                          )
                        : consideration.title,
                      `title-${cindex}`,
                    )}
                  </span>
                  <ul>
                    {(tab === 'Supplements'
                      ? consideration.items.filter(item => !isPromoLine(item))
                      : numberedList
                      ? sortByReferenceNumber(consideration.items)
                      : consideration.items
                    ).map((item, itemIndex) => {
                      return (
                        <li
                          key={`${rootCauseCategory}-${cindex}-${itemIndex}`}
                          // A fully numbered References row prints as a
                          // bibliography; any other row keeps its bullets.
                          style={
                            numberedList ? referenceListItemStyle : undefined
                          }
                        >
                          {tab === 'Supplements'
                            ? renderSupplementItem(item, furtherConsiderations)
                            : tab === 'References'
                            ? renderReferenceItem(
                                item,
                                furtherConsiderations,
                                numberedList,
                              )
                            : renderConsiderationItem(
                                item,
                                citationUrlMap,
                                forPdf,
                              )}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;

    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }

    return this.furtherConsiderationsForPossibleRootCauses(bloodworkAnalysis);
  }
}
